import React, { useEffect } from 'react';
import { useQuery } from 'react-query';
import type { FormDataNode } from '../../types/layout';
import { useFormEngineForm, useFormEngineState } from '../../context/FormEngineContext';
import { FormNodeRenderer } from '../FormNodeRenderer';
import { ActionRegistry } from '@packages/table-builder/registry';
import { Spinner } from '@packages/ui/primitives';

interface FormDataNodeRendererProps {
  node: FormDataNode;
  namePrefix?: string;
}

/**
 * Scoped data-fetching boundary for view layouts.
 *
 * When `node.fetch` is defined, uses ActionRegistry + React Query to lazy-load
 * scoped data and merge it into the parent form state under `node.dataKey`.
 * Children render after data is available.
 *
 * When `node.fetch` is undefined, renders children immediately
 * (data comes from the root hydration).
 */
export const FormDataNodeRenderer = React.memo(function FormDataNodeRenderer({
  node,
  namePrefix,
}: FormDataNodeRendererProps): React.ReactElement {
  const { form } = useFormEngineForm();
  const { params: contextParams } = useFormEngineState();

  const fetchConfig = node.fetch;
  const shouldFetch = fetchConfig != null;

  const resolvedParams = React.useMemo(() => {
    if (!fetchConfig?.params) return {};
    const resolved: Record<string, string> = {};
    for (const [key, value] of Object.entries(fetchConfig.params)) {
      const interpolated = value.replace(/\{params\.([^}]+)\}/g, (_, k: string) => contextParams?.[k] ?? '');
      resolved[key] = interpolated;
    }
    return resolved;
  }, [fetchConfig?.params, contextParams]);

  const { data, isLoading } = useQuery(
    ['view-data', node.dataKey, fetchConfig?.actionId, contextParams?.id],
    () => ActionRegistry.executeData(fetchConfig!.actionId, resolvedParams),
    { enabled: shouldFetch, staleTime: 0, cacheTime: 0 },
  );

  useEffect(() => {
    if (data != null && typeof data === 'object') {
      form.setValue(node.dataKey, data);
    }
  }, [data, form, node.dataKey]);

  if (shouldFetch && isLoading) {
    return (
      <div data-view-data={node.dataKey} style={{ display: 'flex', justifyContent: 'center', padding: '24px 0' }}>
        <Spinner size='sm' />
      </div>
    );
  }

  return (
    <div data-view-data={node.dataKey}>
      {node.children.map((child, idx) => (
        <FormNodeRenderer key={idx} node={child} namePrefix={namePrefix} />
      ))}
    </div>
  );
});
